import { useRef, useState } from "react";
import { Check, ImageUp, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { errMsg, imgSrc, useSite } from "@/site/SiteContext";

const slug = (k) => k.replace(/\./g, "-");

export const EditableField = ({ value, onSave, as: Tag = "p", className = "", testId, multiline = true }) => {
  const { editing } = useSite();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!editing) return <Tag className={className} data-testid={testId}>{value}</Tag>;
  if (draft === null) {
    return (
      <Tag className={`${className} hs-editable`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDraft(value); }} title="Click to edit" data-testid={testId}>
        {value}
        <Pencil className="hs-editable__icon" size={13} aria-hidden="true" />
      </Tag>
    );
  }
  const save = async () => {
    if (!draft.trim()) return toast.error("Text can't be empty");
    setSaving(true);
    try {
      await onSave(draft.trim());
      toast.success("Saved");
      setDraft(null);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="hs-editor" onClick={(e) => e.stopPropagation()} data-testid={`${testId}-editor`}>
      {multiline ? (
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={Math.min(10, Math.max(2, Math.ceil(draft.length / 60)))} autoFocus data-testid={`${testId}-input`} />
      ) : (
        <input value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus data-testid={`${testId}-input`} />
      )}
      <div className="hs-editor__actions">
        <button type="button" onClick={save} disabled={saving} data-testid={`${testId}-save`}><Check size={14} /> {saving ? "Saving…" : "Save"}</button>
        <button type="button" className="is-ghost" onClick={() => setDraft(null)} data-testid={`${testId}-cancel`}><X size={14} /> Cancel</button>
      </div>
    </div>
  );
};

export const T = ({ k, ...props }) => {
  const { content, saveBlock } = useSite();
  return <EditableField value={content.blocks[k]} onSave={(v) => saveBlock(k, v)} testId={`text-${slug(k)}`} {...props} />;
};

export const ReplaceButton = ({ onReplace, testId, label = "Replace image" }) => {
  const { upload } = useSite();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      await onReplace(await upload(file));
      toast.success("Image saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" className="hs-replace" disabled={busy} onClick={(e) => { e.preventDefault(); e.stopPropagation(); ref.current.click(); }} data-testid={testId}>
        <ImageUp size={14} /> {busy ? "Uploading…" : label}
      </button>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={pick} data-testid={`${testId}-file`} />
    </>
  );
};

export const EditableImage = ({ src, alt, className = "", onReplace, testId }) => {
  const { editing } = useSite();
  return (
    <div className={`hs-img ${className}`} data-testid={testId}>
      <img src={imgSrc(src)} alt={alt} loading="lazy" />
      {editing && <ReplaceButton onReplace={onReplace} testId={`${testId}-replace`} />}
    </div>
  );
};

export const BlockImage = ({ k, ...props }) => {
  const { content, saveBlock } = useSite();
  return <EditableImage src={content.blocks[k]} onReplace={(url) => saveBlock(k, url)} testId={`img-${slug(k)}`} {...props} />;
};
